import Application from "../../Application";
import GetAllGenericAttachmentTypesInteractor from "../../attachmentType/interactor/GetAllGenericAttachmentTypesInteractor";
import { ContentTypes } from "../../caseLawDocuments/common/Constants";
import FindCourtCaseDetailsForCaseDecisionInteractor from "../../caseLawDocuments/interactor/FindCourtCaseDetailsForCaseDecisionInteractor";
import { ICourtCase } from "../../caseLawDocuments/model/CourtCase";
import { TAttachmentPresentationModel } from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import GetGenericCodebookInteractor from "../../codebooks/interactor/GetGenericCodebookInteractor";
import GetAllOrganisationAttachmentTypesInteractor from "../../organisations/interactors/GetAllOrganisationAttachmentTypesInteractor";
import GetAllOrganisationsInteractor from "../../organisations/interactors/GetAllOrganisationsInteractor";
import CaseDecisionUpdatePresenter, {
  ICaseDecisionUpdatePresenter
} from "../presenter/CaseDecisionUpdatePresenter";
import { TCaseDecisionPresentationModel } from "../presenter/model/CaseDecisionPresentationModel";
import FindCaseDecisionInteractor from "./FindCaseDecisionInteractor";
import GetAllCaseDecisionTypesInteractor from "./GetAllCaseDecisionTypesInteractor";
import GetAttachmentsForCaseDecisionInteractor from "./GetAttachmentsForCaseDecisionInteractor";
import GetCaseDecisionIndexesInteractor from "./GetCaseDecisionIndexesInteractor";
import GetInvalidatedCaseDecisionsInteractor from "./GetInvalidatedCaseDecisionsInteractor";

export default class ShowCaseDecisionUpdateInteractor {
  private application: Application;
  private output?: ICaseDecisionUpdatePresenter;
  private findCaseDecisionInteractor: FindCaseDecisionInteractor;
  private getGenericCodebookInteractor: GetGenericCodebookInteractor;
  private getAllCaseDecisionTypesInteractor: GetAllCaseDecisionTypesInteractor;
  private getAllOrganisationsInteractor: GetAllOrganisationsInteractor;
  private getAttachmentsForCaseDecisionInteractor: GetAttachmentsForCaseDecisionInteractor;
  private getCaseDecisionIndexesInteractor: GetCaseDecisionIndexesInteractor;
  private getAllOrganisationAttachmentTypesInteractor: GetAllOrganisationAttachmentTypesInteractor;
  private findCourtCaseDetailsForCaseDecisionInteractor: FindCourtCaseDetailsForCaseDecisionInteractor;
  private getAllGenericAttachmentTypesInteractor: GetAllGenericAttachmentTypesInteractor;
  private getInvalidatedCaseDecisionsInteractor: GetInvalidatedCaseDecisionsInteractor;

  constructor({
    application,
    findCaseDecision,
    getGenericCodebook,
    getAllCaseDecisionTypes,
    getAllOrganisations,
    getAttachmentsForCaseDecision,
    getCaseDecisionIndexes,
    getAllOrganisationAttachmentTypes,
    findCourtCaseDetailsForCaseDecision,
    getAllGenericAttachmentTypes,
    getInvalidatedCaseDecisions
  }) {
    this.application = application;
    this.findCaseDecisionInteractor = findCaseDecision;
    this.getGenericCodebookInteractor = getGenericCodebook;
    this.getAllCaseDecisionTypesInteractor = getAllCaseDecisionTypes;
    this.getAllOrganisationsInteractor = getAllOrganisations;
    this.getCaseDecisionIndexesInteractor = getCaseDecisionIndexes;
    this.getAllOrganisationAttachmentTypesInteractor =
      getAllOrganisationAttachmentTypes;
    this.findCourtCaseDetailsForCaseDecisionInteractor =
      findCourtCaseDetailsForCaseDecision;
    this.getAttachmentsForCaseDecisionInteractor =
      getAttachmentsForCaseDecision;
    this.getAllGenericAttachmentTypesInteractor = getAllGenericAttachmentTypes;
    this.getInvalidatedCaseDecisionsInteractor = getInvalidatedCaseDecisions;
  }

  private async getAllOrganisationAttachmentTypes(organisationId: number) {
    return this.getAllOrganisationAttachmentTypesInteractor.execute(
      organisationId
    );
  }
  private async getAllCaseDecisionTypes() {
    return this.getAllCaseDecisionTypesInteractor.execute();
  }
  private async getAllGenericAttachmentTypes() {
    return this.getAllGenericAttachmentTypesInteractor.execute();
  }
  private async getAllOrganisations() {
    return this.getAllOrganisationsInteractor.execute();
  }
  private async getInvalidatedCaseDecisions(id: string) {
    return this.getInvalidatedCaseDecisionsInteractor.execute(id);
  }

  private async getContentTypes() {
    return this.getGenericCodebookInteractor.execute(
      ContentTypes.ATTACHMENT_CONTENT_TYPE,
      false
    );
  }
  private async findCourtCaseDetails(
    courtCaseId: number,
    caseDecisionId: string
  ) {
    return this.findCourtCaseDetailsForCaseDecisionInteractor.execute(
      courtCaseId,
      caseDecisionId
    );
  }
  private async getReferenceLists(
    caseDecisionId: string,
    organisationId: number,
    courtCaseId: number
  ) {
    return Promise.all([
      this.getAllCaseDecisionTypes(),
      this.getAllOrganisations(),
      organisationId
        ? this.getAllOrganisationAttachmentTypes(organisationId)
        : this.getAllGenericAttachmentTypes(),
      this.getContentTypes(),
      this.findCourtCaseDetails(courtCaseId, caseDecisionId),
      this.getInvalidatedCaseDecisions(caseDecisionId)
    ]);
  }
  execute(id: string) {
    this.output = CaseDecisionUpdatePresenter({
      application: this.application,

      initialState: {
        caseDecision: {} as TCaseDecisionPresentationModel,
        organisations: [],
        caseDecisionTypes: [],
        caseDecisionIndexes: [],
        attachmentTypes: [],
        contentTypes: [],
        attachmentList: [],
        uploadedFile: undefined,
        mainAttachment: {} as TAttachmentPresentationModel,
        indexHierarchyList: [],
        deletedAttachments: [],
        attachmentModalVisible: false,
        attachmentType: "",
        courtCase: {} as ICourtCase,
        anonimisedUploadedFile: undefined,
        selectedSecondGroupIdList: [],
        selectedParentGroupIdList: [],
        parentGroupList: [],
        invalidatedCaseDecisions: [],
        invalidatePublishedCaseDecisionsVisible: false,
        publishedCaseDecisions: [],
        secondGroupList: [],
        organisationIndexes: [],
        selectedIndexes: [],
        decisionTypes: [],
        indexDataModalVisible: false
      }
    });
    this.findCaseDecisionInteractor.execute(id).then((caseDecision) => {
      this.getReferenceLists(
        caseDecision.id,
        caseDecision.organisationId,
        caseDecision.courtCaseId
      )
        .then(
          ([
            caseLawDocumentTypes,
            organisations,
            attachmentTypes,
            contentTypes,
            courtCase,
            invalidatedCaseDecisions
          ]) =>
            this.output?.loadReferenceValues(
              caseLawDocumentTypes,
              organisations,
              attachmentTypes,
              contentTypes,
              courtCase,
              invalidatedCaseDecisions
            )
        )
        .then(() => this.getAttachmentsForCaseDecisionInteractor.execute(id))
        .then((attachments) => this.output?.loadAttachments(attachments))
        .then(() => this.getCaseDecisionIndexesInteractor.execute(id))
        .then((organisationIndex) =>
          this.output?.loadCaseDecisionIndexes(organisationIndex)
        )
        .then(() => this.output?.loadCaseDecision(caseDecision));
    });

    return this.output;
  }
}
